import type { RoleCode } from '@/consts/roles';
import { env } from '@/env/env';

import type { User } from './schema';

const getAllPermissions = (user: User): string[] => {
  const fromRoles = user.Roles.flatMap((role) => {
    const perms = [...role.Permissions];
    if (role.UserRights !== undefined && role.UserRights !== null) {
      try {
        const parsed: unknown = JSON.parse(role.UserRights);
        if (Array.isArray(parsed)) {
          perms.push(...parsed.filter((item): item is string => typeof item === 'string'));
        }
      } catch {
        // ignore malformed JSON
      }
    }
    return perms;
  });
  const fromUser = user.Permissions ?? [];
  return [...new Set([...fromUser, ...fromRoles])];
};

export const hasAnyRole = (user: User | null | undefined, roles: RoleCode[]): boolean => {
  if (env.VITE_BYPASS_PERMISSIONS === 'true') return true;
  if (!user?.Roles) return false;
  return user.Roles.some((role) => roles.includes(role.Id as RoleCode));
};

export const hasPermission = (user: User | null | undefined, permission: string): boolean => {
  if (env.VITE_BYPASS_PERMISSIONS === 'true') return true;
  if (!user?.Roles) return false;
  const permLower = permission.toLowerCase();
  return getAllPermissions(user).some((p) => p.toLowerCase() === permLower);
};

export const hasAllPermissions = (user: User | null | undefined, permissions: string[]): boolean => {
  if (env.VITE_BYPASS_PERMISSIONS === 'true') return true;
  if (!user?.Roles) return false;
  if (permissions.length === 0) return true;
  const userPermsLower = new Set(getAllPermissions(user).map((p) => p.toLowerCase()));
  return permissions.every((p) => userPermsLower.has(p.toLowerCase()));
};

export const hasAnyPermission = (user: User | null | undefined, permissions: string[]): boolean => {
  if (env.VITE_BYPASS_PERMISSIONS === 'true') return true;
  if (!user?.Roles) return false;
  if (permissions.length === 0) return false;
  const userPermsLower = new Set(getAllPermissions(user).map((p) => p.toLowerCase()));
  return permissions.some((p) => userPermsLower.has(p.toLowerCase()));
};
